import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { find, get, isEmpty } from 'lodash-es';

import ReportsTable from './ReportsTable';
import VersionsHistoryModal from './VersionsHistoryModal';
import TablePerPageDropdown from '../../TablePerPageDropdown';
import SimpleCard from '../../../../../components/Card/SimpleCard';
import CollapsibleSearch from '../CollapsibleSearch';
import Pagination from '../../../../../components/Pagination';
import { useAppCable } from '../../../../../hooks';
import {
  fetchCustomReportsData,
  fetchVersionsData,
  changeCustomReportsPerPage,
} from '../../../actions';
import {
  customReportsSettingsSelector,
  customReportsFiltersSelector,
  customReportsSelector,
  versionsSelector,
  versionsSettingsSelector,
} from '../../../selectors';
import { initParams, permissions } from '../../../configs';

const { hasFirstVisitAlert } = initParams;

const ReportsTableSection = () => {
  const dispatch = useDispatch();

  const settings = useSelector(customReportsSettingsSelector);
  const filters = useSelector(customReportsFiltersSelector);
  const tableData = useSelector(customReportsSelector);
  const versions = useSelector(versionsSelector);
  const versionsSettings = useSelector(versionsSettingsSelector);

  const [currentCustomReportId, setCurrentCustomReportId] = useState(0);

  const pagination = useMemo(
    () => ({
      total: parseInt(filters.pagination.total, 10),
      per_page: parseInt(filters.pagination.per_page, 10),
      current_page: parseInt(filters.pagination.current_page, 10),
    }),
    [filters.pagination]
  );

  const getCurrentReportVersions = (reportId) =>
    find(versions, { custom_report_id: reportId.toString() });

  const getCurrentReport = (reportId) => find(tableData, { id: reportId });

  const handleVersionsHistoryRequest = (reportId) => {
    dispatch(fetchVersionsData({ custom_report_id: reportId }));
  };
  const handleFiltersChange = (filtersObj) => {
    dispatch(fetchCustomReportsData(filtersObj));
  };

  const handlePerPageChange = useCallback((value) => {
    dispatch(changeCustomReportsPerPage(value));
  }, []);

  const handlePageChange = ({ selected }) => {
    handleFiltersChange({
      pagination: { ...filters.pagination, current_page: selected },
    });
  };

  const handleVersionsHistoryOpen = (reportId) => {
    setCurrentCustomReportId(reportId);
  };

  const handleDownloadEventReceived = (event) => {
    const { custom_s3_report_id } = event;
    if (find(tableData, { id: custom_s3_report_id })) {
      handleFiltersChange(filters);
    }
    if (currentCustomReportId.toString() === custom_s3_report_id.toString()) {
      handleVersionsHistoryRequest(custom_s3_report_id);
    }
  };

  const handleSearchChange = (query) => {
    handleFiltersChange({ query, pagination: { ...filters.pagination, current_page: 0 } });
  };

  useAppCable(
    { channel: 'CustomS3ReportsChannel' },
    {
      onReceived: handleDownloadEventReceived,
    }
  );

  useEffect(() => {
    if (permissions.custom_reports) {
      dispatch(fetchCustomReportsData());
    }
  }, []);

  useEffect(() => {
    if (currentCustomReportId !== 0 && !getCurrentReportVersions(currentCustomReportId)) {
      handleVersionsHistoryRequest(currentCustomReportId);
    }
  }, [currentCustomReportId]);

  const versionHistory = getCurrentReportVersions(currentCustomReportId) || {};
  const showPagination = !isEmpty(tableData) && pagination.total > tableData.length;

  return (
    <>
      <SimpleCard
        headerLeft={
          <div className="stack-vertical gap-4">
            <h2 className="heading-21-130 text-black">Apptopia-generated exports</h2>
            <p className="text-12-150 text-secondary">
              Exports requested by your company, produced by the Apptopia data team
            </p>
          </div>
        }
        headerRight={
          <div className="d-flex gap-8 align-items-center">
            <CollapsibleSearch
              disabled={!filters.query && isEmpty(tableData)}
              onChange={handleSearchChange}
            />
            <TablePerPageDropdown
              active={pagination.per_page}
              total={pagination.total}
              disabled={!filters.query && pagination.total === 0}
              onChange={handlePerPageChange}
            />
          </div>
        }
        contentDisableGutters
        actionsLeft={
          showPagination ? (
            <Pagination
              total={pagination.total}
              perPage={pagination.per_page}
              currentPage={pagination.current_page}
              onPageChange={handlePageChange}
            />
          ) : null
        }
        className="apptopia-generated-exports-card"
      >
        <ReportsTable
          tableData={tableData}
          filters={filters}
          settings={settings}
          initialRowsLimit={pagination.per_page}
          hasFirstVisitAlert={hasFirstVisitAlert}
          onFiltersChange={handleFiltersChange}
          onVersionsHistoryRequest={handleVersionsHistoryOpen}
        />
      </SimpleCard>
      <div className="d-flex gap-8 align-items-center">
        <p className="text-14-150 text-black">Need to export complex data? Our team can help.</p>

        <a
          href="mailto:support@apptopia.com?subject=Request for Custom Reports data&body=Dear Customer Success, I am interested in getting customized data reports from Apptopia. I would like to discuss my requirements with you."
          className="text-primary text-underline"
        >
          Email Customer Success
        </a>
      </div>
      {!!currentCustomReportId && (
        <VersionsHistoryModal
          versionHistory={versionHistory}
          pending={get(versionsSettings, 'pending')}
          open={parseInt(currentCustomReportId, 10) > 0}
          handleClose={() => handleVersionsHistoryOpen(0)}
          report={getCurrentReport(currentCustomReportId)}
        />
      )}
    </>
  );
};

export default ReportsTableSection;
